// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { includes } from 'lodash';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

// Selectors
import { getMeasurementsMapThunk } from '../../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';
import { userLanguagePreferenceSelector } from '../../../user/currentUserSelector';

// Utils
import { hasCommandModifier } from '../../../utils/keyboard/keyboardUtility';
import { isTextLink } from '../../../../common/links/linkUtils';
import isLinkAnEmail from '../../../../common/tiptap/extensions/hyperlink/isLinkAnEmail';
import { handleSaveToClipboardEvent } from './clipboardUtils';
import { getShowCaption } from '../../../../common/elements/utils/elementPropertyUtils';
import measurementsConnect from '../../../components/measurementsStore/measurementsConnect';
import { isCard, isContent } from '../../../../common/elements/utils/elementTypeUtils';
import { getListChildNewLocation } from '../../../../common/elements/utils/elementLocationUtils';
import { parseTableHTMLFromClipboardData } from '../../../element/table/utils/tableDataUtils';
import { getColumnCount, getRowCount } from '../../../../common/table/utils/tableCellDataPropertyUtils';

// Actions
import { pasteCaptions, pasteText } from './clipboardActions';

// Constants
import { KEY_CODES } from '../../../utils/keyboard/keyConstants';
import { ClipboardOperation, DATA_TRANSFER_ITEM_KINDS, DATA_TRANSFER_TYPES } from './clipboardConstants';

// Styles
import './WorkspaceClipboardHandler.scss';

const MAX_CAPTION_URL_LENGTH = 300;

const eventPathPolyfill = (event) => {
    let element = event.target;
    const path = [element];

    if (!element || !element.parentElement) return [];

    while (element.parentElement) {
        element = element.parentElement;
        path.unshift(element);
    }

    return path;
};

const nodeHasClassName = (node, className) => node.className.split(' ').includes(className);

const mapStateToProps = createStructuredSelector({
    userLanguagePreference: userLanguagePreferenceSelector,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchPasteCaptions: ({ text, html, elements }) => dispatch(pasteCaptions({ text, html, elements })),
    dispatchPasteText: ({ text, html, location }) => dispatch(pasteText({ text, html, location })),
});

const mapMeasurementsDispatchToProps = (dispatch) => ({
    dispatchGetMeasurementsMap: () => dispatch(getMeasurementsMapThunk()),
});

@measurementsConnect(null, mapMeasurementsDispatchToProps)
@connect(mapStateToProps, mapDispatchToProps)
class WorkspaceClipboardHandler extends React.Component {
    componentWillMount() {
        document.addEventListener('cut', this.onCut);
        document.addEventListener('copy', this.onCopy);
        document.addEventListener('paste', this.onPaste);
        document.addEventListener('keydown', this.onKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('cut', this.onCut);
        document.removeEventListener('copy', this.onCopy);
        document.removeEventListener('paste', this.onPaste);
        document.removeEventListener('keydown', this.onKeyDown);
    }

    /**
     * Focus the content editable so that Firefox (and I think Safari) can accept the paste events.
     */
    onKeyDown = (event) => {
        if (
            hasCommandModifier(event) &&
            event.target === window.document.body &&
            (event.keyCode === KEY_CODES.CMD_LEFT ||
                event.keyCode === KEY_CODES.CMD_RIGHT ||
                event.keyCode === KEY_CODES.CTRL)
        ) {
            this.clipboardDiv.focus();
        }
    };

    onCut = (event) => {
        handleSaveToClipboardEvent(event, ClipboardOperation.cut, this.props);
    };

    onCopy = (event) => {
        handleSaveToClipboardEvent(event, ClipboardOperation.copy, this.props);
    };

    onPaste = (event) => {
        const { clipboardData } = event;
        const { currentBoardId, isEditable } = this.props;

        const eventPath =
            // chrome
            event.path ||
            // safari
            (event.composedPath && event.composedPath()) ||
            // firefox
            eventPathPolyfill(event);

        // don't handle paste event if:
        if (
            // board isn't editable
            !isEditable ||
            // paste is inside contentEditable field
            (eventPath && eventPath.find((el) => el.contentEditable === 'true')) ||
            // paste is into input field without class name `enable-workspace-clipboard`
            ((event.target.nodeName === 'INPUT' || event.target.nodeName === 'TEXTAREA') &&
                !nodeHasClassName(event.target, 'enable-workspace-clipboard'))
        )
            return;

        const location = getListChildNewLocation({ listId: currentBoardId });

        const handlerData = { clipboardData, location };
        return (
            this.handleTable(handlerData, this.props) ||
            this.handleFile(handlerData) ||
            this.handleElement(handlerData) ||
            this.handleCaptionText(handlerData) ||
            this.handleLink(handlerData) ||
            this.handleHtml(handlerData) ||
            this.handlePlainText(handlerData)
        );
    };

    handleFile = ({ clipboardData, location }) => {
        // If there's no file data transfer type then can't get a file from the pasted data
        if (!includes(clipboardData.types, DATA_TRANSFER_TYPES.FILES)) return;

        const { createShortcutFile } = this.props;

        const text = clipboardData.getData(DATA_TRANSFER_TYPES.PLAIN_TEXT);
        const caption = isTextLink(text) && text.length < MAX_CAPTION_URL_LENGTH ? text : undefined;

        let result;
        const items = [...clipboardData.items];
        items
            .filter((item) => item.kind === DATA_TRANSFER_ITEM_KINDS.FILE)
            .forEach((item) => {
                const file = item.getAsFile();

                if (!file) {
                    console.warn('Unable to read file');
                    result = true;
                    return;
                }

                result = createShortcutFile({ location, file, caption, showCaption: false });
            });

        return result;
    };

    handleElement = ({ clipboardData }) => {
        if (!includes(clipboardData.types, DATA_TRANSFER_TYPES.ELEMENTS)) return;

        const { dispatchGetClipboardSaveId, dispatchSaveToClipBoard, dispatchPasteElements, isEditable } = this.props;

        const dataString = clipboardData.getData(DATA_TRANSFER_TYPES.ELEMENTS);
        const { saveId, elements, elementsBoundingRect, operation, sessionId } = JSON.parse(dataString);
        const clipboardSaveId = dispatchGetClipboardSaveId();

        // Set pasteCount to -1 so that it pastes at the same position on cross browser
        if (saveId !== clipboardSaveId) {
            dispatchSaveToClipBoard({
                operation: operation || ClipboardOperation.copy,
                elements,
                saveId,
                pasteCount: -1,
                elementsBoundingRect,
                isEditable,
            });
        }

        dispatchPasteElements(elements, sessionId);

        return true;
    };

    handleTable = ({ clipboardData, location }, props) => {
        const hasTextClipboardData = includes(clipboardData.types, DATA_TRANSFER_TYPES.PLAIN_TEXT);
        const hasHtmlClipboardData = includes(clipboardData.types, DATA_TRANSFER_TYPES.HTML);

        if (!hasTextClipboardData && !hasHtmlClipboardData) return;

        const { createShortcutTable } = this.props;

        const tableData = parseTableHTMLFromClipboardData(clipboardData);
        if (!tableData) return false;

        const { values, colWidthsPx } = tableData;
        const nRows = getRowCount(values);
        const nCols = getColumnCount(values);

        if (nRows < 2 || nCols < 2) return false;

        // Only create new table if there are at least 2 rows and 2 columns, so that we're sure
        // that user is pasting a table
        createShortcutTable({ location, values, colWidthsPx });
        return true;
    };

    handleCaptionText = ({ clipboardData }) => {
        // If there's no text data transfer type then can't get text from the pasted data
        if (
            !includes(clipboardData.types, DATA_TRANSFER_TYPES.PLAIN_TEXT) &&
            !includes(clipboardData.types, DATA_TRANSFER_TYPES.HTML)
        )
            return;

        const text = clipboardData.getData(DATA_TRANSFER_TYPES.PLAIN_TEXT);
        const html = clipboardData.getData(DATA_TRANSFER_TYPES.HTML);

        if (!text && !html) return;

        const { dispatchGetSelectedElements, dispatchPasteCaptions } = this.props;
        const selectedElements = dispatchGetSelectedElements();
        const filteredElements = selectedElements.filter((el) => isContent(el) && !isCard(el) && !getShowCaption(el));

        if (!filteredElements.size) return;

        return dispatchPasteCaptions({ text, html, elements: filteredElements });
    };

    handleLink = ({ clipboardData, location }) => {
        // If there's no text data transfer type then can't get a link from the pasted data
        if (!includes(clipboardData.types, DATA_TRANSFER_TYPES.PLAIN_TEXT)) return;

        const text = clipboardData.getData(DATA_TRANSFER_TYPES.PLAIN_TEXT);
        if (!isTextLink(text) || text.length > MAX_CAPTION_URL_LENGTH) return;
        if (isLinkAnEmail(text)) return;

        const { createShortcutLink, deselectAll } = this.props;
        const linkElementId = createShortcutLink({ location, url: text });
        deselectAll();
        return linkElementId;
    };

    handleHtml = ({ clipboardData, location }) => {
        // If there's no html data transfer type then can't get HTML from the pasted data
        if (!includes(clipboardData.types, DATA_TRANSFER_TYPES.HTML)) return;

        const { dispatchPasteText } = this.props;
        const html = clipboardData.getData(DATA_TRANSFER_TYPES.HTML);

        if (!html) return;

        return dispatchPasteText({ location, html });
    };

    handlePlainText = ({ clipboardData, location }) => {
        // If there's no text data transfer type then can't get text from the pasted data
        if (!includes(clipboardData.types, DATA_TRANSFER_TYPES.PLAIN_TEXT)) return;

        const { dispatchPasteText } = this.props;
        const text = clipboardData.getData(DATA_TRANSFER_TYPES.PLAIN_TEXT);
        return dispatchPasteText({ location, text });
    };

    render() {
        return (
            <div
                ref={(c) => {
                    this.clipboardDiv = c;
                }}
                className="WorkspaceClipboardHandler"
                contentEditable
                value=" "
            />
        );
    }
}

WorkspaceClipboardHandler.propTypes = {
    isEditable: PropTypes.bool,
    deselectAll: PropTypes.func,
    dispatchGetSelectedElements: PropTypes.func,
    dispatchGetCurrentlyEditingId: PropTypes.func,
    dispatchGetCurrentModalElementId: PropTypes.func,
    dispatchPasteElements: PropTypes.func,
    dispatchSaveToClipBoard: PropTypes.func,
    dispatchGetClipboardSaveId: PropTypes.func,
    dispatchGetMeasurementsMap: PropTypes.func,
    dispatchPasteText: PropTypes.func,
    dispatchPasteCaptions: PropTypes.func,
    createShortcutLink: PropTypes.func,
    createShortcutFile: PropTypes.func,
    createShortcutTable: PropTypes.func,
    currentBoardId: PropTypes.string,
    userLanguagePreference: PropTypes.string,
};

export default WorkspaceClipboardHandler;
